// Exported because any code that clears sessionStorage mid-session (e.g. login
// over an existing session) must preserve it: store.js evaluates getPersistKey()
// once at module load, so regenerating the tab id would leave the store writing
// to one key while clearTabPersist() deletes another.
export const TAB_ID_KEY = '__tab_id__';

function generateTabId() {
  return 'tab_' + Math.random().toString(36).slice(2, 10) + '_' + Date.now();
}

// Returns the persistent tab ID for this tab.
// Created once on first access, then reused for the lifetime of the tab.
export function getTabId() {
  let id = sessionStorage.getItem(TAB_ID_KEY);
  if (!id) {
    id = generateTabId();
    sessionStorage.setItem(TAB_ID_KEY, id);
  }
  return id;
}

// The redux-persist storage key for this tab.
// Scoped only to the tab ID — NOT the user — because this is evaluated once
// at module-load time in store.js and must remain stable for the lifetime
// of the JS bundle.  User isolation is handled by clearing sessionStorage +
// dispatching RESET_STORE on login / logout instead.
let cachedKey = null;

export function getPersistKey() {
  // Captured once per page load. ~60 call sites across the app run
  // sessionStorage.clear() in their 401 handlers, which drops __tab_id__; if
  // the key were recomputed afterwards the store would go on writing to the old
  // key while clearTabPersist() deleted a different one. Caching pins it for
  // the lifetime of the bundle, which is exactly the window store.js needs.
  if (!cachedKey) cachedKey = `root:${getTabId()}`;
  return cachedKey;
}

// Clean up this tab's persist entry when logging out.
// redux-persist stores data under "persist:<key>".
export function clearTabPersist() {
  sessionStorage.removeItem(`persist:${getPersistKey()}`);
}

// The only key that survives every wipe. It is not session data at all — it
// identifies the TAB, and destroying it is what silently empties the store on
// the next page load.
export const PRESERVED_SESSION_KEYS = [TAB_ID_KEY];

// Login, and only login, additionally carries these across its own wipe: they
// are read off the URL for the person signing IN, set before the wipe, and
// needed after it. They belong to the incoming user.
//
// They must NOT survive a logout or a 401. topNav's logout deliberately drops
// the theme so the previous account's setting cannot follow a different person
// into the same tab, and `stripeEmail` is the previous account's email address
// — exactly the same leak, one field over.
export const PRESERVED_LOGIN_KEYS = [
  ...PRESERVED_SESSION_KEYS,
  'stripeEmail',
  'callSource',
  'switchState',
  'appVersion'
];

/**
 * Wipe the session without destroying the tab's identity.
 *
 * A bare `sessionStorage.clear()` takes `__tab_id__` with it, and that is the
 * bug behind Frank M. Booth's vanishing header. getPersistKey() caches the key
 * at module load, so the store keeps writing to `root:<old id>` for the rest of
 * the page's life and nothing looks wrong. The next page load calls getTabId(),
 * finds nothing, mints a NEW id, and redux-persist rehydrates from a key that
 * has never been written — an empty store — while the auth token written after
 * the clear is still valid. The user stays signed in and silently loses their
 * `signIn` slice, which is what thirty components read their role from.
 *
 * That is why login through signIn.jsx was fine and login through signUp.jsx
 * was not: only the former preserved the id. Toshia Vining's 2026-08-20
 * recording is this exact sequence — log in, header correct, reload, header
 * degraded, same tab, still signed in.
 *
 * Use this everywhere `sessionStorage.clear()` was used. Clearing auth is the
 * point; clearing the tab's identity never was.
 */
function clearSessionKeeping(keys) {
  try {
    const preserved = {};
    keys.forEach((key) => {
      const value = sessionStorage.getItem(key);
      if (value !== null) preserved[key] = value;
    });
    sessionStorage.clear();
    Object.entries(preserved).forEach(([key, value]) => sessionStorage.setItem(key, value));
  } catch (e) {
    // Storage throws in some private-browsing modes. A failed wipe must not
    // take the caller's error handling down with it.
  }
}

export function clearSessionPreservingTab() {
  clearSessionKeeping(PRESERVED_SESSION_KEYS);
}

/**
 * The login variant. Same wipe, plus the URL-derived values that belong to the
 * user signing in. Use only where the incoming session set them first.
 */
export function clearSessionForLogin() {
  clearSessionKeeping(PRESERVED_LOGIN_KEYS);
}

/**
 * Device-level display preferences, namespaced so the logout wipe can spare
 * them. Anything under this prefix must be per-DEVICE and non-identifying: the
 * canvas review column width qualifies, because the usable width depends on the
 * monitor rather than on the person, and it is already keyed per user so two
 * people sharing a workstation cannot inherit each other's.
 *
 * A setting that belongs to the person rather than the machine does NOT go here
 * — the theme is the example, and it is deliberately dropped at logout and
 * restored from the backend profile instead.
 */
export const LOCAL_PREFERENCE_PREFIX = 'bh.';

/**
 * The localStorage counterpart of clearSessionPreservingTab.
 *
 * Logout must still wipe every trace of the account — this repo has already
 * shipped one cross-tenant bleed caused by state surviving in localStorage — so
 * this clears exactly as much as `localStorage.clear()` did, then puts back only
 * the keys under LOCAL_PREFERENCE_PREFIX.
 */
export function clearLocalPreservingPreferences() {
  try {
    const preserved = {};
    Object.keys(localStorage)
      .filter((key) => key.startsWith(LOCAL_PREFERENCE_PREFIX))
      .forEach((key) => {
        preserved[key] = localStorage.getItem(key);
      });
    localStorage.clear();
    Object.entries(preserved).forEach(([key, value]) => localStorage.setItem(key, value));
  } catch (e) {
    // Storage throws in some private-browsing modes. A failed wipe must not
    // take the caller's error handling down with it.
  }
}
